import { useState, useEffect } from 'preact/hooks'
import { apiClient, InitData, assetUrl } from '../../core'
import { t } from '../../core/i18n'

interface Room {
  id: string
  name: string
  description?: string | null
  imageUrl?: string | null
  address?: string | null
  city?: string | null
  postalCode?: string | null
  capacity?: number | null
  latitude?: number | null
  longitude?: number | null
  features?: string[] | null
  specialEquipment?: string[] | null
}

interface Props {
  initData: InitData
  title?: string | null
  showFeatures?: boolean
}

/**
 * RoomsWidget — mirrors HS-native room cards.
 * Card layout: image (or fallback gradient with 🏠) at top, room name,
 * address with 📍, optional description, capacity meta, and "Vis på kort"
 * link if coordinates are present.
 */
// initData reserved for future tenant-aware deep links.
export function RoomsWidget({ initData: _initData, title = null, showFeatures = true }: Props) {
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetch() {
      const result = await apiClient.get<Room[]>('/api/widget/rooms')
      if (result.data) setRooms(result.data)
      if (result.error) setError(result.error)
      setLoading(false)
    }
    fetch()
  }, [])

  if (loading) {
    // Skeleton — 2 placeholder cards with shimmer
    return (
      <div>
        <style>{styles}</style>
        <div class="hsw-rooms-grid">
          {[0, 1].map(i => (
            <div key={i} class="hsw-room-card">
              <div class="hsw-room-img-skel" />
              <div class="hsw-room-body">
                <div class="hsw-skel hsw-skel-name" />
                <div class="hsw-skel hsw-skel-addr" />
                <div class="hsw-skel hsw-skel-line" />
                <div class="hsw-skel hsw-skel-line" style={{ width: '60%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (!rooms.length) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏠</div>
        <p>{t(_initData.locale).noRooms}</p>
      </div>
    )
  }

  return (
    <div>
      <style>{styles}</style>
      {title && <h2 class="hsw-rooms-title">{title}</h2>}
      <div class="hsw-rooms-grid">
        {rooms.map(room => {
          const fullAddress = [
            room.address,
            [room.postalCode, room.city].filter(Boolean).join(' '),
          ]
            .filter(s => s && String(s).trim().length > 0)
            .join(', ')

          const hasCoords =
            room.latitude !== null &&
            room.latitude !== undefined &&
            room.longitude !== null &&
            room.longitude !== undefined

          const mapHref = hasCoords
            ? `https://www.google.com/maps/search/?api=1&query=${room.latitude},${room.longitude}`
            : fullAddress
              ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
              : null

          const tags = [
            ...new Set([
              ...(room.features || []),
              ...(room.specialEquipment || []),
            ]),
          ].filter(t => t && t.trim().length > 0)

          return (
            <div key={room.id} class="hsw-room-card">
              {room.imageUrl ? (
                <img
                  src={assetUrl(room.imageUrl)}
                  alt={room.name}
                  class="hsw-room-img"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div class="hsw-room-img hsw-room-img-fallback">
                  <span class="hsw-room-img-icon" aria-hidden="true">🏠</span>
                </div>
              )}
              <div class="hsw-room-body">
                <h3 class="hsw-room-name">{room.name}</h3>

                {fullAddress && (
                  <div class="hsw-room-addr">
                    <span class="hsw-room-pin">📍</span>
                    <span>{fullAddress}</span>
                  </div>
                )}

                {room.description && (
                  <p class="hsw-room-desc">{room.description}</p>
                )}

                {showFeatures && tags.length > 0 && (
                  <div class="hsw-room-tags">
                    {tags.map((tag, i) => (
                      <span key={i} class="hsw-room-tag">{tag}</span>
                    ))}
                  </div>
                )}

                <div class="hsw-room-meta">
                  {room.capacity !== null && room.capacity !== undefined && room.capacity > 0 && (
                    <span class="hsw-room-meta-item">
                      Plads til {room.capacity} personer
                    </span>
                  )}
                  {mapHref && (
                    <a
                      class="hsw-room-map-link"
                      href={mapHref}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(_initData.locale).showOnMap} →
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const styles = `
  .hsw-rooms-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--yoga-text, #0a0a0a);
    text-align: center;
    margin: 0 auto 2rem;
    max-width: 1200px;
    letter-spacing: -0.01em;
  }
  .hsw-rooms-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }
  .hsw-room-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all .3s;
  }
  .hsw-room-card:hover {
    border-color: rgba(0,0,0,0.2);
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  .hsw-room-img {
    display: block;
    width: 100%;
    height: 200px;
    object-fit: cover;
  }
  .hsw-room-img-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(255,107,53,0.12), rgba(26,26,46,0.08));
    color: var(--yoga-primary, #FF6B35);
  }
  .hsw-room-img-icon {
    font-size: 3rem;
    line-height: 1;
    opacity: 0.85;
  }
  .hsw-room-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    flex: 1;
  }
  .hsw-room-name {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
  }
  .hsw-room-addr {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    font-size: 0.85rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 0.75rem;
    line-height: 1.4;
  }
  .hsw-room-pin {
    flex-shrink: 0;
    line-height: 1.4;
  }
  .hsw-room-desc {
    color: var(--yoga-text-muted, #525252);
    font-size: 0.9rem;
    line-height: 1.6;
    margin: 0 0 0.75rem;
  }
  .hsw-room-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin: 0 0 0.75rem;
  }
  .hsw-room-tag {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 99px;
    background: rgba(255,107,53,0.1);
    color: var(--yoga-primary, #FF6B35);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
  .hsw-room-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.75rem;
    border-top: 1px solid var(--yoga-card-border, #f0f0f0);
  }
  .hsw-room-meta-item {
    font-size: 0.78rem;
    color: var(--yoga-text-muted, #737373);
    font-weight: 500;
  }
  .hsw-room-map-link {
    font-size: 0.78rem;
    color: var(--yoga-primary, #FF6B35);
    text-decoration: none;
    font-weight: 600;
    margin-left: auto;
    transition: opacity .2s;
  }
  .hsw-room-map-link:hover {
    opacity: 0.75;
  }
  /* Skeletons */
  .hsw-room-img-skel {
    width: 100%;
    height: 200px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
  }
  .hsw-skel {
    height: 14px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsw-shimmer 1.5s infinite;
    margin-bottom: 0.6rem;
    border-radius: 4px;
  }
  .hsw-skel-name { width: 55%; height: 22px; margin-bottom: 0.85rem; }
  .hsw-skel-addr { width: 80%; height: 14px; margin-bottom: 1rem; }
  .hsw-skel-line { width: 100%; }
  @keyframes hsw-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`
